import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Table,
  TablePaginationConfig,
  Form,
  message,
  Modal,
  Space,
  Popconfirm,
  Input,
  Select,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as S from "./styles";
import api from "../../../service/api";
import CategoriaForm from "../CadastroCategoria/CategoriaForm";
import EmpresaMultiSelect from "../EmpresaMultiSelect";
import "antd/dist/antd.css";

// 1. Interface para os dados retornados pela API (em camelCase)
interface CategoriaDetalheAPI {
  id: number;
  categoriaId: string;
  grupo: string;
  descricao: string;
  excecao: string;
  categoria: string;
  pagamentos: number | null;
  empresas: string[];
}

// 2. Interface para os dados que o front-end usará (em PascalCase)
interface CategoriaDetalheItem {
  id: number;
  CategoriaId: string;
  Grupo: string;
  Descricao: string;
  Excecao: string;
  categoria: string;
  Pagamentos: number | null;
  Empresas: string[];
}

// Opção de pagamento (periodicidade) vinda do enum do back-end (GET /CategoriaDetalhe/pagamentos)
interface PagamentoOption {
  value: number;
  label: string;
}

// Empresa do grupo (GET /CategoriaDetalhe/empresas-by-group). O value é o CNPJ.
interface EmpresaOption {
  value: string;
  label: string;
}

// Interface ATUALIZADA para o item retornado pela API /Extratos/get-all-filter-by-key
interface FiltroDescricaoItem {
  key: number; // Chave para o React
  categoria: string; // Valor da Categoria
  descricao: string; // Descrição completa
  chave: string; // Chave real encontrada no texto (para copiar)
}

// 3. Interface da Modal de Filtro (MANTIDA)
interface FiltroDescricaoModalProps {
  open: boolean;
  onClose: () => void;
  initialSearchTerm: string;
  grupoDoCliente: string | null;
}

interface Props {
  currentUser?: any;
  onClose: () => void;
}

// --- Função para Gerar Cor Baseada na Chave (Hash Simples) ---
const getHighlightColor = (key: string): string => {
  if (!key) return '#ffff00'; // Amarelo padrão se a chave for vazia

  // Lista de cores para alternância visual
  const colors = [
    '#FFEB3B', // Amarelo
  ];

  // Calcula um índice baseado na chave (hash simples)
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};
// ----------------------------------------------------
// --- Componente da Modal de Busca (Filtro) ---
// ----------------------------------------------------
const FiltroDescricaoModal: React.FC<FiltroDescricaoModalProps> = ({
  open,
  onClose,
  initialSearchTerm,
  grupoDoCliente,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(initialSearchTerm);
  const [uniqueDescriptions, setUniqueDescriptions] = useState<FiltroDescricaoItem[]>([]); // Tipo atualizado
  const [isLoadingModal, setIsLoadingModal] = useState(false);

  // Função para copiar a CHAVE REAL (KeyNoTexto)
  const handleCopyText = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
      message.success(`Chave '${text}' copiada para a área de transferência!`);
    } else {
      // Fallback para ambientes não seguros (http)
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`Chave '${text}' copiada (via fallback)!`);
      } catch (err) {
        message.error('Falha ao copiar o texto.');
      }
      document.body.removeChild(textArea);
    }
  };

  // Colunas da tabela de descrições únicas (ORDEM AJUSTADA)
  const columns = [
    {
      title: "CATEGORIA",
      dataIndex: "categoria",
      key: "categoria",
      width: 150,
    },
    {
      title: "DESCRIÇÃO NO EXTRATO",
      dataIndex: "descricao",
      key: "descricao",
      // IMPLEMENTAÇÃO DA LÓGICA DE DESTAQUE COLORIDO
      render: (text: string, record: FiltroDescricaoItem) => {
        if (!record.chave || !text) {
          return text; // Retorna o texto normal se não houver chave
        }

        const highlightColor = getHighlightColor(record.chave);
        const regex = new RegExp(`(${record.chave})`, 'gi');

        // Divide o texto usando a chave e mapeia, aplicando o estilo
        const parts = text.split(regex).filter(String);

        return (
          <span>
            {parts.map((part, index) =>
              // Se a parte casar com a regex (ou seja, for a chave)
              regex.test(part) ? (
                <span
                  key={index}
                  style={{
                    backgroundColor: highlightColor,
                    fontWeight: 'bold',
                    padding: '1px 3px', // Pequeno padding para visualização
                    borderRadius: '2px'
                  }}
                >
                  {part}
                </span>
              ) : (
                <span key={index}>{part}</span>
              )
            )}
          </span>
        );
      },
    },
    {
      title: "AÇÃO",
      key: "acao",
      width: 150,
      render: (text: string, record: FiltroDescricaoItem) => (
        <Button
          type="link"
          icon={<CopyOutlined />}
          // CHAVE COPIADA: Usa record.chave
          onClick={() => handleCopyText(record.chave)}
        >
          Copiar Chave
        </Button>
      ),
    },
  ];

  // Função para buscar as descrições únicas na API
  const fetchUniqueDescriptions = useCallback(async (key: string) => {
    // ... (lógica de verificação e chamada da API)

    if (!key || key.length < 3 || !grupoDoCliente) {
      setUniqueDescriptions([]);
      if (key.length >= 3) {
        message.warning("Grupo de cliente não definido ou termo de busca inválido.");
      }
      return;
    }

    setIsLoadingModal(true);
    try {
      // O tipo de retorno deve corresponder à interface FiltroDescricaoItem
      const response = await api.get<FiltroDescricaoItem[]>(
        "/Extratos/get-all-filter-by-key",
        {
          params: {
            group: grupoDoCliente,
            key: key,
          },
        }
      );

      // Mapeamento dos dados recebidos para adicionar a chave React
      const formattedData: FiltroDescricaoItem[] = response.data.map((item, index) => ({
        ...item,
        key: index,
      }));

      setUniqueDescriptions(formattedData);
    } catch (error) {
      toast.error("Erro ao buscar descrições únicas.");
      setUniqueDescriptions([]);
    } finally {
      setIsLoadingModal(false);
    }
  }, [grupoDoCliente]);

  // 1. Efeito para INICIALIZAR o campo de busca (roda só quando a modal abre)
  useEffect(() => {
    if (open) {
      setLocalSearchTerm(initialSearchTerm);
    }
  }, [open, initialSearchTerm]);

  // Ação de busca manual (botão ou Enter) - APENAS CHAMA A BUSCA
  const handleSearchClick = () => {
    if (localSearchTerm && localSearchTerm.length >= 3) {
      fetchUniqueDescriptions(localSearchTerm);
    } else {
      message.warning("Digite pelo menos 3 caracteres para buscar.");
    }
  };


  return (
    <Modal
      title="Buscar Descrição"
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Fechar
        </Button>,
        <Button
          key="search"
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearchClick}
          disabled={!localSearchTerm || localSearchTerm.length < 3}
        >
          Buscar
        </Button>,
      ]}
      wrapClassName="my-custom-rounded-modal"
    >
      <Space direction="vertical" style={{ width: "100%", padding: "20px 0", marginTop: "-20px", marginBottom: "-20px" }}>
        {/* Input para digitação */}
        <Input
          placeholder="Digite a descrição"
          size="large"
          prefix={<SearchOutlined />}
          value={localSearchTerm}
          onChange={(e) => setLocalSearchTerm(e.target.value)}
          onPressEnter={handleSearchClick}
        />

        {/* Tabela de Resultados da API */}
        <Table
          columns={columns}
          dataSource={uniqueDescriptions}
          loading={isLoadingModal}
          pagination={false}
          // A rowKey agora usa a combinação de Descricao e Categoria (já que Descricao pode não ser única)
          rowKey={(record) => `${record.descricao}-${record.categoria}-${record.chave}`}
          locale={{ emptyText: "Use o botão 'Buscar' ou Enter para carregar as descrições únicas" }}
          size="small"
          style={{ maxHeight: '400px', overflowY: 'auto' }}
        />
      </Space>
    </Modal>
  );
};
// ----------------------------------------------------

const ListaCategoria: React.FC<Props> = ({ onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<CategoriaDetalheItem[]>([]);
  const [unfilteredData, setUnfilteredData] = useState<CategoriaDetalheItem[]>([]);
  const [grupoDoCliente, setGrupoDoCliente] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [form] = Form.useForm();

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CategoriaDetalheItem | null>(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [pagamentosOptions, setPagamentosOptions] = useState<PagamentoOption[]>([]);
  const [empresasOptions, setEmpresasOptions] = useState<EmpresaOption[]>([]);


  const openListaExtratos = () => {
    const cliente = localStorage.getItem("cliente"); // <-- CORRIGIDO
    if (!cliente) {
      message.warning("Nenhum cliente logado para ver extratos.");
      return;
    }

    try {
      const clienteObj = JSON.parse(cliente);
      const grupo = clienteObj?.nomeDoGrupo;

      if (!grupo) {
        message.error("Dados do cliente incompletos para processar os extratos.");
        return;
      }

      const key = 'processando';
      message.loading({ content: 'Processando extratos em segundo plano...', key, duration: 0 });

      api.post("CategoriaDetalhe/processar-detalhes-extratos", null, { params: { group: grupo } })
        .then(() => {
          message.success({ content: 'Extratos processados com sucesso!', key, duration: 3 });
          onClose();
        })
        .catch(() => {
          message.error({ content: 'Erro ao processar os extratos. Tente novamente mais tarde.', key, duration: 3 });
        });
    } catch (error) {
      console.error("Erro ao processar extratos:", error);
      message.error({ content: 'Erro ao processar os extratos. Tente novamente mais tarde.', key: 'processando', duration: 2 });
    }
  };

  useEffect(() => {
    const cliente = localStorage.getItem("cliente"); // <-- CORRIGIDO
    if (cliente) {
      try {
        const clienteObj = JSON.parse(cliente);
        if (clienteObj && clienteObj.nomeDoGrupo) {
          setGrupoDoCliente(clienteObj.nomeDoGrupo);
        }
      } catch (e) {
        console.error("Erro ao fazer parse do cliente de localStorage:", e);
        toast.error("Erro ao carregar dados do cliente.");
      }
    }
  }, []);

  const fetchData = useCallback(async (searchFilter: string = "") => {
    if (!grupoDoCliente) {
      setData([]);
      setUnfilteredData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    const params = {
      group: grupoDoCliente,
      ...(searchFilter && { search: searchFilter })
    };

    try {
      const response = await api.get<CategoriaDetalheAPI[]>(
        "/CategoriaDetalhe/get-all-category-detalhe-by-group",
        { params: params }
      );

      const mappedData: CategoriaDetalheItem[] = response.data.map(item => ({
        id: item.id,
        CategoriaId: item.categoria,
        Grupo: item.grupo,
        Descricao: item.descricao,
        Excecao: item.excecao,
        categoria: item.categoria,
        Pagamentos: item.pagamentos,
        Empresas: item.empresas ?? [],
      }));

      if (!searchFilter) {
        setUnfilteredData(mappedData);
      }

      setData(mappedData);
      setSearchTerm(searchFilter);
      setCurrentPage(1);
    } catch (error) {
      toast.error("Erro ao buscar dados.");
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [grupoDoCliente]);

  useEffect(() => {
    if (grupoDoCliente) {
      fetchData("");
    }
  }, [grupoDoCliente, fetchData]);


  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
  };

  const handleFiltroDescricao = () => {
    setIsFilterModalOpen(true);
  };

  // Função que recebe o termo selecionado/digitado da modal e aplica o filtro
  const handleApplyFilter = (term: string) => {
    fetchData(term);
  };

  const handleCadastrarClick = () => {
    setEditingItem(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = (record: CategoriaDetalheItem) => {
    setEditingItem(record);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (record: CategoriaDetalheItem) => {
    try {
      await api.delete(`/CategoriaDetalhe/delete-category-detalhe/${record.id}`);
      toast.success("Item excluído com sucesso!");
      fetchData(searchTerm);
    } catch (error) {
      toast.error("Erro ao excluir item.");
      console.error("Erro ao excluir item:", error);
    }
  };

  const handleCancelForm = () => {
    setIsFormModalOpen(false);
    setEditingItem(null);
  };

  const handleFormSuccess = () => {
    fetchData(searchTerm);
    handleCancelForm();
  };

  // Opções do combobox de pagamentos (periodicidade) vêm do enum do back-end, para front e
  // back compartilharem os mesmos valores.
  useEffect(() => {
    api.get<PagamentoOption[]>("/CategoriaDetalhe/pagamentos")
      .then(response => setPagamentosOptions(response.data))
      .catch(() => toast.error("Erro ao carregar as opções de pagamento."));
  }, []);

  // Empresas do grupo para o multi-select "Empresas" (value = CNPJ, label = nome da empresa).
  useEffect(() => {
    if (!grupoDoCliente) return;

    api.get<{ cnpj: string; empresa: string }[]>("/CategoriaDetalhe/empresas-by-group", {
      params: { group: grupoDoCliente },
    })
      .then(response => setEmpresasOptions(response.data.map(e => ({ value: e.cnpj, label: e.empresa }))))
      .catch(() => toast.error("Erro ao carregar as empresas do grupo."));
  }, [grupoDoCliente]);

  // Persiste uma edição inline reenviando o DTO completo (o endpoint de update espera todos os
  // campos; mandar parcial apagaria os demais). Recebe só o que mudou e mescla com o registro.
  const aplicarNaLinha = (id: number, valor: CategoriaDetalheItem) => {
    setData(prev => prev.map(item => item.id === id ? valor : item));
    setUnfilteredData(prev => prev.map(item => item.id === id ? valor : item));
  };

  const salvarCategoriaDetalhe = async (
    record: CategoriaDetalheItem,
    changes: Partial<Pick<CategoriaDetalheItem, "Pagamentos" | "Empresas">>
  ) => {
    const atualizado = { ...record, ...changes };

    // Otimista: reflete na tela imediatamente (o combobox não trava esperando o servidor)
    // e desfaz se o salvamento falhar.
    aplicarNaLinha(record.id, atualizado);

    try {
      await api.put("/CategoriaDetalhe/update-category-detalhe", {
        Id: atualizado.id,
        CategoriaId: 0,
        categoriaName: atualizado.categoria,
        Grupo: atualizado.Grupo,
        Descricao: atualizado.Descricao,
        Excecao: atualizado.Excecao,
        Pagamentos: atualizado.Pagamentos,
        Empresas: atualizado.Empresas,
      });
    } catch (error) {
      aplicarNaLinha(record.id, record);
      throw error;
    }
  };

  const handlePagamentoChange = async (record: CategoriaDetalheItem, pagamento: number | null) => {
    try {
      await salvarCategoriaDetalhe(record, { Pagamentos: pagamento });
    } catch (error) {
      toast.error("Erro ao atualizar o pagamento.");
      console.error("Erro ao atualizar o pagamento:", error);
    }
  };

  const handleEmpresasChange = async (record: CategoriaDetalheItem, cnpjs: string[]) => {
    try {
      await salvarCategoriaDetalhe(record, { Empresas: cnpjs });
    } catch (error) {
      toast.error("Erro ao atualizar as empresas.");
      console.error("Erro ao atualizar as empresas:", error);
    }
  };

  const columns = [
    { title: "CATEGORIA", dataIndex: "categoria", key: "categoria", width: 160 },
    {
      dataIndex: "Descricao",
      key: "descricao",
      ellipsis: { showTitle: true },
      title: (
        <Space>
          DESCRIÇÃO NO EXTRATO
          {searchTerm && <SearchOutlined style={{ color: '#1890ff' }} />}
        </Space>
      )
    },
    { title: "EXCEÇÕES", dataIndex: "Excecao", key: "excecao", width: 200, ellipsis: { showTitle: true } },
    {
      title: "PAGAMENTOS",
      dataIndex: "Pagamentos",
      key: "pagamentos",
      width: 130,
      render: (_: unknown, record: CategoriaDetalheItem) => (
        <Select
          style={{ width: 110 }}
          placeholder="Selecione"
          allowClear
          value={record.Pagamentos ?? undefined}
          options={pagamentosOptions}
          onChange={(value) => handlePagamentoChange(record, value ?? null)}
        />
      ),
    },
    {
      title: "EMPRESAS",
      dataIndex: "Empresas",
      key: "empresas",
      width: 340,
      render: (_: unknown, record: CategoriaDetalheItem) => (
        <EmpresaMultiSelect
          style={{ width: "100%" }}
          options={empresasOptions}
          value={record.Empresas ?? []}
          onChange={(values) => handleEmpresasChange(record, values)}
        />
      ),
    },
    {
      title: "AÇÕES",
      key: "acoes",
      width: 100,
      render: (text: string, record: CategoriaDetalheItem) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleEditClick(record)}
            icon={<EditOutlined />}
          />
          <Popconfirm
            title={`Tem certeza que deseja excluir o item '${record.Descricao}'?`}
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
            onConfirm={() => handleDelete(record)}
            okText="Excluir"
            okType="danger"
            cancelText="Cancelar"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ height: "calc(100vh - 100px)", overflowY: "auto", paddingRight: "10px" }}>
      <S.Container>
        <S.RoundedModalStyle />
        <S.EmpresaDropdownStyle />
        <S.DivTable>
          <S.Tools>
            <Form
              form={form}
              layout="inline"
              style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
            >
              <Form.Item>
                <Space>
                  <Button
                    onClick={openListaExtratos}
                    type="primary"
                    style={{
                      borderRadius: "4px",
                      backgroundColor: "#01B73A",
                      borderColor: "#01B73A",
                    }}
                  >
                    Processar e fechar
                  </Button>
                  <Button
                    onClick={handleCadastrarClick}
                    type="primary"
                    style={{ borderRadius: "4px" }}
                  >
                    Cadastrar Nova Categoria e Detalhes
                  </Button>
                  <Button
                    onClick={handleFiltroDescricao}
                    type="primary"
                    icon={<SearchOutlined />}
                    style={{ borderRadius: "4px" }}
                  >
                    Filtrar Descrição
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </S.Tools>

          <Table
            columns={columns}
            dataSource={data}
            loading={isLoading}
            rowKey={(record) => record.id}
            pagination={{
              current: currentPage,
              pageSize,
              total: data.length,
              showQuickJumper: true,
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
          />
        </S.DivTable>
        <Modal
          title={editingItem ? "Editar Detalhe de Categoria" : "Cadastrar nova Categoria e Detalhes"}
          open={isFormModalOpen}
          onCancel={handleCancelForm}
          footer={null}
          wrapClassName="my-custom-rounded-modal"
        >
          <CategoriaForm onSuccess={handleFormSuccess} itemToEdit={editingItem} />
        </Modal>

        {/* CHAMADA DA MODAL DE FILTRO AJUSTADA */}
        <FiltroDescricaoModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          initialSearchTerm={searchTerm}
          grupoDoCliente={grupoDoCliente}
        />
        {/* FIM CHAMADA DA MODAL DE FILTRO */}

      </S.Container>
    </div>
  );
};

export default ListaCategoria;